<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { toast } from 'dashboard/helper/toast';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Multiselect from 'vue-multiselect';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const { accountScopedRoute } = useAccount();

const allProjects = useMapGetter('studioProjects/getRecords');
const uiFlags = useMapGetter('studioProjects/getUIFlags');

const isLinking = ref(false);
// Opcao selecionada no Multiselect (objeto { value, label, ... } ou null).
const selectedOption = ref(null);
// true = mostrar o seletor mesmo havendo projeto vinculado (modo "trocar").
const isReplacing = ref(false);

// Projeto de texto NÃO tem canal: qualquer projeto de texto atende qualquer
// caixa que o Studio suporta (`inbox.channel_key` presente — WhatsApp oficial,
// Baileys, Instagram, Facebook, Site). O runtime desenha cada mensagem pela
// capacidade da caixa. O fluxo efetivo vem da versão ativa (resolvido no
// backend); aqui só cuidamos do vínculo.
const channelSupported = computed(() => !!props.inbox.channel_key);

const eligibleProjects = computed(() =>
  allProjects.value.filter(p => p.kind === 'text' && !p.archived)
);

// Projeto vinculado — `inboxes.ai_project_id` aponta pro projeto.
const linkedProject = computed(
  () =>
    eligibleProjects.value.find(p => p.id === props.inbox.ai_project_id) || null
);

const projectOptions = computed(() =>
  eligibleProjects.value.map(p => ({
    value: p.id,
    label: p.name,
    published: !!p.production_version_id,
    versionNumber: p.production_version_number,
  }))
);

const statusBadge = project => {
  if (project?.production_version_id) {
    return {
      text: t('INBOX_MGMT.AI_CONFIG.PUBLISHED', {
        number: project.production_version_number,
      }),
      class: 'bg-n-teal-3 text-n-teal-11',
    };
  }
  return {
    text: t('INBOX_MGMT.AI_CONFIG.DRAFT'),
    class: 'bg-n-amber-3 text-n-amber-11',
  };
};

const linkedProjectRoute = computed(() => {
  if (!linkedProject.value) return null;
  return accountScopedRoute('studio_project_detail', {
    projectId: linkedProject.value.id,
  });
});

// Mostra o seletor quando nao ha projeto vinculado ou quando o admin clicou
// "Trocar projeto". Caixa de canal que o Studio não atende não ganha seletor
// (o backend recusaria o vínculo) — a tela diz por quê.
const showSelector = computed(
  () => channelSupported.value && (!linkedProject.value || isReplacing.value)
);

onMounted(() => {
  store.dispatch('studioProjects/get');
  // Re-busca a inbox pra nao mostrar um ai_project_id em cache. O vínculo é
  // explícito por esta aba (inboxes.ai_project_id) — o auto-link por
  // triggerInbox no publish foi removido.
  store.dispatch('inboxes/get');
});

const setInboxProject = projectId =>
  store.dispatch('inboxes/updateInbox', {
    id: props.inbox.id,
    formData: false,
    ai_project_id: projectId,
  });

const startReplacing = () => {
  isReplacing.value = true;
  selectedOption.value = null;
};

const cancelReplacing = () => {
  isReplacing.value = false;
  selectedOption.value = null;
};

const linkProject = async () => {
  const projectId = selectedOption.value?.value;
  if (!projectId) return;

  isLinking.value = true;
  try {
    await setInboxProject(projectId);
    selectedOption.value = null;
    isReplacing.value = false;
    toast.success(t('INBOX_MGMT.AI_CONFIG.LINKED'));
  } catch {
    toast.error(t('INBOX_MGMT.AI_CONFIG.ERROR_LINK'));
  } finally {
    isLinking.value = false;
  }
};

const unlinkProject = async () => {
  if (!linkedProject.value) return;

  isLinking.value = true;
  try {
    await setInboxProject(null);
    selectedOption.value = null;
    isReplacing.value = false;
    toast.success(t('INBOX_MGMT.AI_CONFIG.UNLINKED'));
  } catch {
    toast.error(t('INBOX_MGMT.AI_CONFIG.ERROR_UNLINK'));
  } finally {
    isLinking.value = false;
  }
};
</script>

<template>
  <div class="mx-8">
    <LoadingState v-if="uiFlags.fetchingList && allProjects.length === 0" />
    <div v-else class="flex flex-wrap mx-0">
      <SettingsSection
        :title="t('INBOX_MGMT.AI_CONFIG.TITLE')"
        :sub-title="t('INBOX_MGMT.AI_CONFIG.DESCRIPTION')"
      >
        <div class="space-y-4">
          <!-- Projeto vinculado (modo "visualizar") -->
          <template v-if="linkedProject && !isReplacing">
            <div
              class="flex items-center gap-3 p-3 rounded-lg border border-solid border-n-teal-6 bg-n-teal-2"
            >
              <span class="i-lucide-folder-kanban size-5 text-n-teal-11" />
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <p class="text-sm font-medium text-n-slate-12 truncate">
                    {{ linkedProject.name }}
                  </p>
                  <span
                    class="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-full"
                    :class="statusBadge(linkedProject).class"
                  >
                    {{ statusBadge(linkedProject).text }}
                  </span>
                </div>
                <p
                  v-if="!linkedProject.production_version_id"
                  class="mt-0.5 text-xs text-n-amber-11"
                >
                  {{ t('INBOX_MGMT.AI_CONFIG.NO_ACTIVE_VERSION') }}
                </p>
                <p
                  v-else-if="linkedProject.description"
                  class="mt-0.5 text-xs text-n-slate-10 truncate"
                >
                  {{ linkedProject.description }}
                </p>
              </div>
              <router-link
                :to="linkedProjectRoute"
                class="text-xs text-n-blue-11 hover:underline whitespace-nowrap"
              >
                {{ t('INBOX_MGMT.AI_CONFIG.EDIT_PROJECT') }}
              </router-link>
            </div>

            <div class="flex gap-2">
              <NextButton
                type="button"
                :label="t('INBOX_MGMT.AI_CONFIG.REPLACE')"
                :disabled="isLinking"
                @click="startReplacing"
              />
              <NextButton
                type="button"
                :label="t('INBOX_MGMT.AI_CONFIG.UNLINK')"
                :disabled="isLinking"
                :is-loading="isLinking"
                faded
                ruby
                @click="unlinkProject"
              />
            </div>
          </template>

          <!-- Canal que o Studio não atende: diz isso em vez de oferecer nada -->
          <div
            v-else-if="!channelSupported"
            class="flex items-start gap-2 rounded-lg border border-solid border-n-amber-6 bg-n-amber-3 px-3 py-2 text-sm text-n-amber-11"
          >
            <span class="i-lucide-triangle-alert mt-0.5 size-4 shrink-0" />
            <p>{{ t('INBOX_MGMT.AI_CONFIG.CHANNEL_NOT_SUPPORTED') }}</p>
          </div>

          <!-- Seletor (sem projeto vinculado, ou trocando) -->
          <template v-else-if="showSelector">
            <div v-if="projectOptions.length > 0" class="space-y-3">
              <div class="block">
                <span class="block mb-1 text-sm font-medium text-n-slate-12">
                  {{ t('INBOX_MGMT.AI_CONFIG.SELECT_LABEL') }}
                </span>
                <Multiselect
                  v-model="selectedOption"
                  :options="projectOptions"
                  track-by="value"
                  label="label"
                  :allow-empty="false"
                  :show-labels="false"
                  searchable
                  :placeholder="t('INBOX_MGMT.AI_CONFIG.PLACEHOLDER')"
                >
                  <template #option="{ option }">
                    <div class="flex items-center justify-between gap-2">
                      <span class="truncate">{{ option.label }}</span>
                      <span
                        class="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-full"
                        :class="
                          option.published
                            ? 'bg-n-teal-3 text-n-teal-11'
                            : 'bg-n-amber-3 text-n-amber-11'
                        "
                      >
                        {{
                          option.published
                            ? t('INBOX_MGMT.AI_CONFIG.PUBLISHED', {
                                number: option.versionNumber,
                              })
                            : t('INBOX_MGMT.AI_CONFIG.DRAFT')
                        }}
                      </span>
                    </div>
                  </template>
                </Multiselect>
              </div>
              <div class="flex gap-2">
                <NextButton
                  type="button"
                  :label="
                    linkedProject
                      ? t('INBOX_MGMT.AI_CONFIG.SAVE')
                      : t('INBOX_MGMT.AI_CONFIG.LINK')
                  "
                  :disabled="!selectedOption || isLinking"
                  :is-loading="isLinking"
                  @click="linkProject"
                />
                <NextButton
                  v-if="linkedProject"
                  type="button"
                  :label="t('INBOX_MGMT.AI_CONFIG.CANCEL')"
                  faded
                  :disabled="isLinking"
                  @click="cancelReplacing"
                />
              </div>
            </div>
            <div v-else class="text-sm text-n-slate-10">
              <p>{{ t('INBOX_MGMT.AI_CONFIG.EMPTY') }}</p>
              <router-link
                :to="accountScopedRoute('studio_projects')"
                class="text-n-blue-11 hover:underline"
              >
                {{ t('INBOX_MGMT.AI_CONFIG.CREATE') }}
              </router-link>
            </div>
          </template>
        </div>
      </SettingsSection>
    </div>
  </div>
</template>
